import { MaterialCommunityIcons } from "@expo/vector-icons";
import { CHIME_CONFIRMATION_LABEL, checkBankAnswer, type BankCheck } from "@taskhealth/shared";
import { useFocusEffect, useRouter } from "expo-router";
import { useCallback, useEffect, useRef, useState } from "react";
import {
    View,
    StyleSheet,
    TextInput,
    TouchableOpacity,
    ActivityIndicator,
    Platform,
    Modal,
    Linking
} from "react-native";
import { Text, Icon } from "react-native-paper";

import { SignatureModal } from "../questions/SignatureModal";

import { CameraCapture } from "./CameraCapture";
import { ContractDocument, looksLikeContractMarkdown } from "./ContractDocument";
import { DocumentUpload } from "./DocumentUpload";
import { DatePickerField } from "./inputs/DatePickerField";
import { MenuSelect } from "./inputs/MenuSelect";
import { WebSignaturePad } from "./inputs/WebSignaturePad";

import { isPlausibleEmail, suggestEmailCorrection } from "@/lib/email-input";
import { isSearchableLicense, normalizeLicenseInput } from "@/lib/license-input";
import type { ObItemWithSubmission, ObItemOption } from "@/lib/onboarding-api";
import {
    saveOnboardingAnswers,
    triggerEmailVerification,
    triggerLicenseValidation
} from "@/lib/onboarding-api";
import { colors } from "@/lib/theme";
import {
    getTrainingBundle,
    ORIENTATION_BUNDLE_SLUG,
    type BundleProgress
} from "@/lib/training-api";
import { isVerificationStale } from "@/lib/verification-state";

interface OnboardingItemProps {
    item: ObItemWithSubmission;
    value: string | undefined;
    onChange: (value: string) => void;
    readOnly?: boolean;
    hideStatus?: boolean;
    rnId?: string;
    onUploadComplete?: () => void;
    /** Blocks a document upload with a reason (e.g. a prerequisite field). */
    blockedReason?: string;
    /** She has ticked "this is not a Chime account" for a partner-bank answer. */
    bankConfirmed?: boolean;
    onBankConfirm?: (confirmed: boolean) => void;
}

// ---------------------------------------------------------------------------
// Individual item type components
// ---------------------------------------------------------------------------

function RadioInput({
    options,
    value,
    onChange,
    readOnly
}: {
    options: ObItemOption[];
    value: string | undefined;
    onChange: (v: string) => void;
    readOnly?: boolean;
}) {
    return (
        <View style={styles.optionsContainer}>
            {options.map((opt) => (
                <TouchableOpacity
                    key={opt.id}
                    style={[styles.radioRow, value === opt.value && styles.radioRowSelected]}
                    onPress={() => !readOnly && onChange(opt.value)}
                    disabled={readOnly}
                >
                    <View
                        style={[
                            styles.radioCircle,
                            value === opt.value && styles.radioCircleSelected
                        ]}
                    >
                        {value === opt.value && <View style={styles.radioDot} />}
                    </View>
                    <Text style={styles.radioLabel}>{opt.label}</Text>
                </TouchableOpacity>
            ))}
        </View>
    );
}

const YES_NO_OPTIONS: ObItemOption[] = [
    { id: "yes", itemId: "", label: "Yes", value: "Yes", orderIndex: 0 },
    { id: "no", itemId: "", label: "No", value: "No", orderIndex: 1 }
];

// The 3 most common languages among our nurses (by RN data), pinned to the top
// of the languages list in this exact order; the rest sit behind "View more".
const COMMON_LANGUAGES = ["English", "Russian", "Spanish"];
const PRIMARY_OPTIONS = new Set(COMMON_LANGUAGES);
const MAX_COLLAPSED = 5;

function CheckboxInput({
    options,
    value,
    onChange,
    readOnly
}: {
    options: ObItemOption[];
    value: string | undefined;
    onChange: (v: string) => void;
    readOnly?: boolean;
}) {
    const selected = new Set((value ?? "").split(",").filter(Boolean));
    const [expanded, setExpanded] = useState(false);

    function toggle(optValue: string) {
        if (readOnly) {
            return;
        }
        const next = new Set(selected);
        if (next.has(optValue)) {
            next.delete(optValue);
        } else {
            next.add(optValue);
        }
        onChange(Array.from(next).join(","));
    }

    const hasManyOptions = options.length > MAX_COLLAPSED;
    const primaryOpts = hasManyOptions
        ? options
              .filter((o) => PRIMARY_OPTIONS.has(o.label))
              .sort((a, b) => COMMON_LANGUAGES.indexOf(a.label) - COMMON_LANGUAGES.indexOf(b.label))
        : options;
    const secondaryOpts = hasManyOptions
        ? options.filter((o) => !PRIMARY_OPTIONS.has(o.label))
        : [];
    const hasSelectedSecondary = secondaryOpts.some((o) => selected.has(o.value));
    const showAll = !hasManyOptions || expanded || hasSelectedSecondary;
    // Keep the common languages pinned on top even once the list is expanded.
    const visibleOpts = showAll ? [...primaryOpts, ...secondaryOpts] : primaryOpts;

    return (
        <View style={styles.optionsContainer}>
            {visibleOpts.map((opt) => (
                <TouchableOpacity
                    key={opt.id}
                    style={[styles.radioRow, selected.has(opt.value) && styles.radioRowSelected]}
                    onPress={() => toggle(opt.value)}
                    disabled={readOnly}
                >
                    <View
                        style={[
                            styles.checkbox,
                            selected.has(opt.value) && styles.checkboxSelected
                        ]}
                    >
                        {selected.has(opt.value) && (
                            <Icon source="check" size={14} color="#FFFFFF" />
                        )}
                    </View>
                    <Text style={styles.radioLabel}>{opt.label}</Text>
                </TouchableOpacity>
            ))}
            {hasManyOptions && !showAll && (
                <TouchableOpacity style={styles.viewMoreButton} onPress={() => setExpanded(true)}>
                    <Text style={styles.viewMoreText}>View more ({secondaryOpts.length})</Text>
                    <Icon source="chevron-down" size={16} color={colors.primary} />
                </TouchableOpacity>
            )}
            {hasManyOptions && expanded && (
                <TouchableOpacity style={styles.viewMoreButton} onPress={() => setExpanded(false)}>
                    <Text style={styles.viewMoreText}>Show less</Text>
                    <Icon source="chevron-up" size={16} color={colors.primary} />
                </TouchableOpacity>
            )}
        </View>
    );
}

function BasicTextInput({
    value,
    onChange,
    placeholder,
    readOnly,
    keyboardType,
    autoCapitalize
}: {
    value: string | undefined;
    onChange: (v: string) => void;
    placeholder?: string;
    readOnly?: boolean;
    keyboardType?: "default" | "email-address" | "numeric" | "phone-pad";
    autoCapitalize?: "none" | "sentences" | "words";
}) {
    return (
        <TextInput
            style={[styles.textInput, readOnly && styles.textInputReadOnly]}
            value={value ?? ""}
            onChangeText={onChange}
            placeholder={placeholder}
            placeholderTextColor={colors.textTertiary}
            editable={!readOnly}
            keyboardType={keyboardType}
            autoCapitalize={autoCapitalize}
        />
    );
}

function SSNInput({
    value,
    onChange,
    readOnly
}: {
    value: string | undefined;
    onChange: (v: string) => void;
    readOnly?: boolean;
}) {
    function formatSSN(raw: string): string {
        const digits = raw.replace(/\D/g, "").slice(0, 9);
        if (digits.length <= 3) {
            return digits;
        }
        if (digits.length <= 5) {
            return `${digits.slice(0, 3)}-${digits.slice(3)}`;
        }
        return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
    }

    function handleChange(text: string) {
        const digits = text.replace(/\D/g, "").slice(0, 9);
        onChange(digits);
    }

    return (
        <TextInput
            style={[styles.textInput, readOnly && styles.textInputReadOnly]}
            value={formatSSN(value ?? "")}
            onChangeText={handleChange}
            placeholder="XXX-XX-XXXX"
            placeholderTextColor={colors.textTertiary}
            editable={!readOnly}
            keyboardType="numeric"
            secureTextEntry
        />
    );
}

function DateInput({
    value,
    onChange,
    readOnly
}: {
    value: string | undefined;
    onChange: (v: string) => void;
    readOnly?: boolean;
}) {
    return <DatePickerField value={value} onChange={onChange} readOnly={readOnly} />;
}

function DropdownInput({
    options,
    value,
    onChange,
    readOnly
}: {
    options: ObItemOption[];
    value: string | undefined;
    onChange: (v: string) => void;
    readOnly?: boolean;
}) {
    return (
        <MenuSelect
            value={value}
            options={options.map((o) => ({ label: o.label, value: o.value }))}
            onChange={onChange}
            readOnly={readOnly}
            placeholder="Select an option"
        />
    );
}

/** Renders text, turning any http(s) URLs into tappable links (e.g. the
 *  "where to get this document for free" links in requirement descriptions). */
function LinkifiedText({ text, style }: { text: string; style?: object }) {
    const parts = text.split(/(https?:\/\/[^\s]+)/g);
    return (
        <Text style={style}>
            {parts.map((part, i) =>
                /^https?:\/\//.test(part) ? (
                    <Text
                        key={i}
                        style={styles.link}
                        onPress={() => {
                            void Linking.openURL(part).catch(() => {});
                        }}
                    >
                        {part}
                    </Text>
                ) : (
                    part
                )
            )}
        </Text>
    );
}

/**
 * The onboarding gate for a course bundle.
 *
 * Shows her real progress and a way in. There is nothing to answer — the item
 * is satisfied only when `rn_course_completions` holds a row for every course,
 * which is why this replaced the old self-reported yes/no.
 */
function CourseBundleLauncher({ rnId }: { rnId?: string }) {
    const router = useRouter();
    const [progress, setProgress] = useState<BundleProgress | null>(null);
    const [failed, setFailed] = useState(false);

    const load = useCallback(() => {
        if (!rnId) {
            return;
        }
        getTrainingBundle(Number(rnId), ORIENTATION_BUNDLE_SLUG)
            .then((r) => setProgress(r.progress))
            .catch(() => setFailed(true));
    }, [rnId]);

    // Mount load; `rnId` may still be resolving on first render.
    useEffect(() => {
        load();
    }, [load]);

    // Refresh on focus so finishing a course updates this without a reload.
    useFocusEffect(useCallback(() => load(), [load]));

    if (failed) {
        return <Text style={courseStyles.muted}>Couldn&apos;t load your training right now.</Text>;
    }
    if (!progress) {
        return <ActivityIndicator color={colors.primary} />;
    }

    const done = progress.isComplete;

    return (
        <View style={courseStyles.card}>
            <View style={courseStyles.row}>
                <MaterialCommunityIcons
                    name={done ? "check-circle" : "school-outline"}
                    size={22}
                    color={done ? colors.success : colors.primary}
                />
                <View style={courseStyles.body}>
                    <Text style={courseStyles.title}>
                        {done ? "Orientation complete" : "Complete your orientation"}
                    </Text>
                    <Text style={courseStyles.meta}>
                        {progress.completed} of {progress.total} courses done
                    </Text>
                </View>
            </View>

            <View style={courseStyles.bar}>
                <View
                    style={[
                        courseStyles.barFill,
                        {
                            width: `${
                                progress.total > 0 ? (progress.completed / progress.total) * 100 : 0
                            }%`
                        }
                    ]}
                />
            </View>

            {!done ? (
                <TouchableOpacity
                    style={courseStyles.cta}
                    onPress={() => router.push("/training")}
                    activeOpacity={0.85}
                >
                    <Text style={courseStyles.ctaText}>
                        {progress.completed > 0 ? "Continue orientation" : "Start orientation"}
                    </Text>
                </TouchableOpacity>
            ) : null}
        </View>
    );
}

const courseStyles = StyleSheet.create({
    card: {
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: 14,
        padding: 16,
        gap: 12,
        backgroundColor: colors.surface
    },
    row: { flexDirection: "row", alignItems: "center", gap: 12 },
    body: { flex: 1, minWidth: 0 },
    title: { fontSize: 16, fontWeight: "700", color: colors.ink },
    meta: { fontSize: 13, color: colors.textSecondary, marginTop: 2 },
    muted: { fontSize: 14, color: colors.textSecondary },
    bar: { height: 8, borderRadius: 4, backgroundColor: colors.border, overflow: "hidden" },
    barFill: { height: 8, borderRadius: 4, backgroundColor: colors.primary },
    cta: {
        backgroundColor: colors.primary,
        borderRadius: 12,
        paddingVertical: 13,
        alignItems: "center"
    },
    ctaText: { color: "#FFFFFF", fontWeight: "700", fontSize: 15 }
});

function PlainTextDisplay({ item }: { item: ObItemWithSubmission }) {
    // Long-form legal text (the contract) is authored as markdown and gets the
    // structured reader — collapsible sections, real tables, bold clause leads.
    // Everything else stays a plain linkified paragraph, which is all a short
    // declination statement or instruction needs.
    if (looksLikeContractMarkdown(item.description)) {
        return <ContractDocument markdown={item.description ?? ""} />;
    }
    return (
        <View style={styles.plainTextContainer}>
            <LinkifiedText text={item.description ?? ""} style={styles.plainText} />
        </View>
    );
}

function SignatureInput({
    value,
    onChange,
    readOnly,
    signed
}: {
    value: string | undefined;
    onChange: (v: string) => void;
    readOnly?: boolean;
    /** True when the underlying submission is already approved/acknowledged,
     * so we show "Signed" even if the stored value isn't a fresh data: image. */
    signed?: boolean;
}) {
    const [showModal, setShowModal] = useState(false);
    const isWeb = Platform.OS === "web";
    const isSigned = signed || (!!value && value.length > 0);

    const handleSign = () => {
        if (readOnly) {
            return;
        }
        setShowModal(true);
    };

    const handleDone = (base64: string) => {
        onChange(base64);
        setShowModal(false);
    };

    return (
        <View>
            {isSigned ? (
                <View style={styles.signedContainer}>
                    <View style={styles.signedBadge}>
                        <Icon source="check-circle" size={20} color={colors.success} />
                        <Text style={styles.signedText}>Signed</Text>
                    </View>
                    {!readOnly && (
                        <TouchableOpacity style={styles.resignButton} onPress={handleSign}>
                            <Text style={styles.resignText}>Re-sign</Text>
                        </TouchableOpacity>
                    )}
                </View>
            ) : (
                <TouchableOpacity
                    style={styles.signButton}
                    onPress={handleSign}
                    disabled={readOnly}
                >
                    <Icon source="draw" size={20} color="#FFFFFF" />
                    <Text style={styles.signButtonText}>Sign</Text>
                </TouchableOpacity>
            )}
            {isWeb ? (
                <WebSignaturePad
                    visible={showModal}
                    title="Sign here"
                    onDone={handleDone}
                    onClose={() => setShowModal(false)}
                />
            ) : (
                <SignatureModal
                    visible={showModal}
                    title="Sign here"
                    onDone={handleDone}
                    onClose={() => setShowModal(false)}
                />
            )}
        </View>
    );
}

// ---------------------------------------------------------------------------
// Email input with validation button
// ---------------------------------------------------------------------------

function EmailInputWithValidation({
    item,
    value,
    onChange,
    readOnly,
    rnId
}: {
    item: ObItemWithSubmission;
    value: string | undefined;
    onChange: (v: string) => void;
    readOnly?: boolean;
    rnId?: string;
}) {
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showModal, setShowModal] = useState(false);
    //  `item` is the parent's fetched copy and nothing refetches it after we
    //  send. Without this the send succeeded, the email went out, and the
    //  screen carried on showing "Validate Email" as though nothing had
    //  happened — the confirmation only appeared after force-quitting and
    //  reopening the app, because that is what refetched the submission.
    const [justSent, setJustSent] = useState(false);

    const status = item.submission?.status;
    const isApproved = status === "approved";
    const isRejected = status === "rejected" || status === "verification_failed";
    //  Optimistic, and safe to be: we only set `justSent` after the request
    //  resolved, so the server really is waiting on her inbox. It yields the
    //  moment the server has a real answer, so a later approval or rejection is
    //  never masked by a stale local flag.
    //  Bounded, or it is not a state at all but a trap. A `submitted` row means
    //  the verification email went out and we are waiting on her inbox — true
    //  for a few minutes, and false forever after for anyone whose address
    //  never receives it. 25 applicants sat here, 15 for over a month.
    const stillWaiting =
        status === "submitted" && !isVerificationStale(item.submission?.submittedAt);
    const isVerifying = stillWaiting || (justSent && !isApproved && !isRejected);

    //  Offered, never applied for her. A nurse whose address genuinely is
    //  unusual has to be able to ignore this and send anyway.
    const suggestion = !isApproved && !isVerifying ? suggestEmailCorrection(value ?? "") : null;

    async function handleValidate() {
        if (!rnId || !value) {
            return;
        }
        setError(null);

        if (!isPlausibleEmail(value)) {
            setError("Please enter a valid email address");
            return;
        }

        setSending(true);
        try {
            await saveOnboardingAnswers(rnId, [{ itemId: item.id, answerValue: value }]);
            await triggerEmailVerification(rnId, item.id);
            setJustSent(true);
            setShowModal(true);
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : "Failed to send verification email");
        } finally {
            setSending(false);
        }
    }

    return (
        <View>
            <TextInput
                style={[styles.textInput, (readOnly || isApproved) && styles.textInputReadOnly]}
                value={value ?? ""}
                onChangeText={onChange}
                placeholder="email@example.com"
                placeholderTextColor={colors.textTertiary}
                editable={!readOnly && !isApproved && !isVerifying}
                keyboardType="email-address"
                autoCapitalize="none"
            />
            {error && <Text style={styles.emailError}>{error}</Text>}
            {suggestion && (
                <TouchableOpacity
                    onPress={() => onChange(suggestion)}
                    accessibilityRole="button"
                    accessibilityLabel={`Use the corrected address ${suggestion}`}
                >
                    <Text style={styles.emailSuggestion}>
                        Did you mean <Text style={styles.emailSuggestionStrong}>{suggestion}</Text>?
                        Tap to use it.
                    </Text>
                </TouchableOpacity>
            )}
            {isApproved ? (
                <View style={styles.emailVerifiedRow}>
                    <Icon source="check-circle" size={16} color={colors.success} />
                    <Text style={[styles.emailStatusText, { color: colors.success }]}>
                        Email verified
                    </Text>
                </View>
            ) : isVerifying ? (
                <View>
                    <View style={styles.emailSentBanner}>
                        <Icon source="email-check-outline" size={18} color={colors.success} />
                        <Text style={styles.emailSentBannerText}>
                            Verification email sent to {value}. Please check your email (and your
                            spam folder) and tap the link there to verify your email.
                        </Text>
                    </View>
                    <View style={styles.verificationRow}>
                        <ActivityIndicator size="small" color={colors.warning} />
                        <Text
                            style={[
                                styles.emailStatusText,
                                { color: colors.warning, marginTop: 0 }
                            ]}
                        >
                            Waiting for you to confirm from your email…
                        </Text>
                    </View>
                    <TouchableOpacity
                        style={[styles.validateButton, sending && styles.validateButtonDisabled]}
                        onPress={handleValidate}
                        disabled={sending}
                    >
                        {sending ? (
                            <ActivityIndicator size="small" color="#FFFFFF" />
                        ) : (
                            <Text style={styles.validateButtonText}>Resend Email</Text>
                        )}
                    </TouchableOpacity>
                </View>
            ) : isRejected ? (
                <View>
                    {item.submission?.rejectionReason && (
                        <View style={styles.rejectionBox}>
                            <Text style={styles.rejectionText}>
                                {item.submission.rejectionReason}
                            </Text>
                        </View>
                    )}
                    <TouchableOpacity
                        style={[
                            styles.validateButton,
                            (!value || sending) && styles.validateButtonDisabled
                        ]}
                        onPress={handleValidate}
                        disabled={!value || sending}
                    >
                        {sending ? (
                            <ActivityIndicator size="small" color="#FFFFFF" />
                        ) : (
                            <Text style={styles.validateButtonText}>Retry Validation</Text>
                        )}
                    </TouchableOpacity>
                </View>
            ) : (
                <TouchableOpacity
                    style={[
                        styles.validateButton,
                        (!value || sending) && styles.validateButtonDisabled
                    ]}
                    onPress={handleValidate}
                    disabled={!value || sending}
                >
                    {sending ? (
                        <ActivityIndicator size="small" color="#FFFFFF" />
                    ) : (
                        <Text style={styles.validateButtonText}>Validate Email</Text>
                    )}
                </TouchableOpacity>
            )}

            {/* Confirmation modal */}
            <Modal visible={showModal} transparent animationType="fade">
                <View style={modalStyles.overlay}>
                    <View style={modalStyles.card}>
                        <TouchableOpacity
                            style={modalStyles.closeButton}
                            onPress={() => setShowModal(false)}
                        >
                            <MaterialCommunityIcons
                                name="close"
                                size={18}
                                color={colors.textSecondary}
                            />
                        </TouchableOpacity>
                        <View style={modalStyles.iconCircle}>
                            <MaterialCommunityIcons
                                name="email-check-outline"
                                size={40}
                                color={colors.primary}
                            />
                        </View>
                        <Text style={modalStyles.title}>Email Sent!</Text>
                        <Text style={modalStyles.body}>
                            A verification email was sent to{"\n"}
                            <Text style={modalStyles.highlight}>{value}</Text>
                        </Text>
                        <Text style={modalStyles.hint}>
                            Please check your inbox and click the confirmation button in the email.
                            You can continue the application now.
                        </Text>
                        <TouchableOpacity
                            style={modalStyles.button}
                            onPress={() => setShowModal(false)}
                        >
                            <Text style={modalStyles.buttonText}>Got it</Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </Modal>
        </View>
    );
}

// ---------------------------------------------------------------------------
// License input with validation button
// ---------------------------------------------------------------------------

//  Says "as printed" rather than "6-digit": the old copy told a nurse holding
//  an N-prefixed license that her own license number was invalid.
const LICENSE_TOO_SHORT =
    "Enter your license number exactly as printed, including any leading zeros or letter.";

function LicenseInputWithValidation({
    item,
    value,
    onChange,
    readOnly,
    rnId
}: {
    item: ObItemWithSubmission;
    value: string | undefined;
    onChange: (v: string) => void;
    readOnly?: boolean;
    rnId?: string;
}) {
    const [error, setError] = useState<string | null>(null);
    const [showModal, setShowModal] = useState(false);
    // Persists until WebSocket delivers a NEW result (reviewedAt changes)
    const [waitingForResult, setWaitingForResult] = useState(false);
    const retriedAtRef = useRef<string | null>(null);
    const status = item.submission?.status;
    const isApproved = status === "approved";
    //  Only treat a verifying row as in-flight while it plausibly is. A stale
    //  one leaves the field read-only behind a spinner that never resolves,
    //  and the field is the only way to correct the number.
    const isVerifying =
        (status === "ai_verifying" || status === "submitted") &&
        !isVerificationStale(item.submission?.submittedAt);
    const isRejected = status === "rejected";

    // Detect when a NEW result arrives after retry
    if (waitingForResult && (isApproved || isRejected || isVerifying)) {
        const currentReviewedAt = item.submission?.reviewedAt ?? "";
        // If reviewedAt changed OR server moved to ai_verifying/submitted, it's a new cycle
        if (isVerifying || currentReviewedAt !== retriedAtRef.current) {
            if (!isVerifying) {
                setWaitingForResult(false);
            }
        }
    }

    const showSpinner = waitingForResult || isVerifying;

    // Step 1: "Retry Validation" just opens the modal
    function handleRetryPress() {
        setShowModal(true);
    }

    // Step 2: "Validate" button inside modal → call API + close modal + show spinner
    async function handleModalValidate() {
        if (!rnId || !value) {
            return;
        }
        setError(null);

        const license = normalizeLicenseInput(value);
        if (!isSearchableLicense(license)) {
            setShowModal(false);
            setError(LICENSE_TOO_SHORT);
            return;
        }

        // Snapshot reviewedAt so we detect when a NEW result arrives
        retriedAtRef.current = item.submission?.reviewedAt ?? "";
        setShowModal(false);
        setWaitingForResult(true);
        try {
            await triggerLicenseValidation(rnId, item.id, license);
        } catch (err: unknown) {
            setWaitingForResult(false);
            setError(err instanceof Error ? err.message : "Failed to validate license");
        }
    }

    // First-time validation (no prior result)
    async function handleFirstValidate() {
        if (!rnId || !value) {
            return;
        }
        setError(null);

        const license = normalizeLicenseInput(value);
        if (!isSearchableLicense(license)) {
            setError(LICENSE_TOO_SHORT);
            return;
        }

        retriedAtRef.current = item.submission?.reviewedAt ?? "";
        setWaitingForResult(true);
        try {
            await triggerLicenseValidation(rnId, item.id, license);
            setShowModal(true);
        } catch (err: unknown) {
            setWaitingForResult(false);
            setError(err instanceof Error ? err.message : "Failed to validate license");
        }
    }

    return (
        <View>
            <TextInput
                style={[
                    styles.textInput,
                    (readOnly || isApproved || showSpinner) && styles.textInputReadOnly
                ]}
                value={value ?? ""}
                onChangeText={onChange}
                placeholder="License number, e.g. 123456 or N12345"
                placeholderTextColor={colors.textTertiary}
                editable={!readOnly && !isApproved && !showSpinner}
                //  Not `numeric`: NYSED licenses can carry a leading letter,
                //  and a numeric keypad gives the nurse no way to type it.
                keyboardType="default"
                autoCapitalize="characters"
                autoCorrect={false}
                maxLength={12}
            />
            {error && <Text style={styles.emailError}>{error}</Text>}
            {isApproved ? (
                <View style={styles.emailVerifiedRow}>
                    <Icon source="check-circle" size={16} color={colors.success} />
                    <Text style={[styles.emailStatusText, { color: colors.success }]}>
                        License verified
                    </Text>
                </View>
            ) : showSpinner ? (
                <View style={styles.verificationRow}>
                    <ActivityIndicator size="small" color={colors.warning} />
                    <Text style={[styles.emailStatusText, { color: colors.warning, marginTop: 0 }]}>
                        Validating with NYSED...
                    </Text>
                </View>
            ) : isRejected ? (
                <View>
                    {item.submission?.rejectionReason && (
                        <View style={styles.rejectionBox}>
                            <Text style={styles.rejectionText}>
                                {item.submission.rejectionReason}
                            </Text>
                        </View>
                    )}
                    <TouchableOpacity
                        style={[styles.validateButton, !value && styles.validateButtonDisabled]}
                        onPress={handleRetryPress}
                        disabled={!value}
                    >
                        <Text style={styles.validateButtonText}>Retry Validation</Text>
                    </TouchableOpacity>
                </View>
            ) : (
                <TouchableOpacity
                    style={[styles.validateButton, !value && styles.validateButtonDisabled]}
                    onPress={handleFirstValidate}
                    disabled={!value}
                >
                    <Text style={styles.validateButtonText}>Validate License</Text>
                </TouchableOpacity>
            )}

            {/* License validation modal */}
            <Modal visible={showModal} transparent animationType="fade">
                <View style={modalStyles.overlay}>
                    <View style={modalStyles.card}>
                        <TouchableOpacity
                            style={modalStyles.closeButton}
                            onPress={() => setShowModal(false)}
                        >
                            <MaterialCommunityIcons
                                name="close"
                                size={18}
                                color={colors.textSecondary}
                            />
                        </TouchableOpacity>
                        <View style={modalStyles.iconCircle}>
                            <MaterialCommunityIcons
                                name="shield-search"
                                size={40}
                                color={colors.primary}
                            />
                        </View>
                        <Text style={modalStyles.title}>Validate License</Text>
                        <Text style={modalStyles.body}>
                            We'll verify your license number{"\n"}
                            <Text style={modalStyles.highlight}>{value}</Text> with NYSED.
                        </Text>
                        <Text style={modalStyles.hint}>
                            This may take a moment. We'll update the status automatically.
                        </Text>
                        <TouchableOpacity style={modalStyles.button} onPress={handleModalValidate}>
                            <Text style={modalStyles.buttonText}>Validate</Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </Modal>
        </View>
    );
}

// ---------------------------------------------------------------------------
// Verification status indicator
// ---------------------------------------------------------------------------

function VerificationIndicator({ status }: { status: string }) {
    if (status === "ai_verifying") {
        return (
            <View style={styles.verificationRow}>
                <Icon source="loading" size={14} color={colors.warning} />
                <Text style={[styles.verificationText, { color: colors.warning }]}>
                    Verifying...
                </Text>
            </View>
        );
    }
    if (status === "approved") {
        return (
            <View style={styles.verificationRow}>
                <Icon source="check-circle" size={14} color={colors.success} />
                <Text style={[styles.verificationText, { color: colors.success }]}>Approved</Text>
            </View>
        );
    }
    if (status === "rejected") {
        return (
            <View style={styles.verificationRow}>
                <Icon source="close-circle" size={14} color={colors.error} />
                <Text style={[styles.verificationText, { color: colors.error }]}>Rejected</Text>
            </View>
        );
    }
    return null;
}

/**
 * What we say about a bank answer, under the field, as she types it.
 *
 * Two shapes, because we know two different things (see unsupported-bank.ts):
 *
 *  - `block` — she named Chime. Red, final, no way past it. An account we
 *    accept here is one payroll cannot pay into, and she would find that out
 *    only after working her first visits. The API refuses it too.
 *
 *  - `confirm` — a partner-bank routing number. Amber, and a question rather
 *    than a verdict: those banks back Chime AND a lot of other accounts, so
 *    most nurses who see this are fine and simply tick the box. Refusing them
 *    outright would stop a payable nurse from onboarding at all.
 */
function BankAnswerNotice({
    check,
    confirmed,
    onConfirm,
    readOnly
}: {
    check: BankCheck;
    confirmed: boolean;
    onConfirm: (v: boolean) => void;
    readOnly?: boolean;
}) {
    const blocking = check.level === "block";
    return (
        <View
            style={[
                styles.bankNoticeBox,
                blocking ? styles.bankNoticeBlock : styles.bankNoticeWarn
            ]}
            accessibilityRole="alert"
            accessibilityLiveRegion="assertive"
        >
            <View style={styles.bankNoticeRow}>
                <Icon
                    source={blocking ? "alert-octagon" : "alert"}
                    size={18}
                    color={blocking ? colors.error600 : colors.warning600}
                />
                <Text
                    style={[
                        styles.bankNoticeText,
                        { color: blocking ? colors.error600 : colors.warning600 }
                    ]}
                >
                    {check.message}
                </Text>
            </View>
            {!blocking && (
                <TouchableOpacity
                    style={styles.bankConfirmRow}
                    onPress={() => !readOnly && onConfirm(!confirmed)}
                    disabled={readOnly}
                    accessibilityRole="checkbox"
                    accessibilityState={{ checked: confirmed }}
                    accessibilityLabel={CHIME_CONFIRMATION_LABEL}
                >
                    <View style={[styles.checkbox, confirmed && styles.checkboxSelected]}>
                        {confirmed && <Icon source="check" size={14} color="#FFFFFF" />}
                    </View>
                    <Text style={styles.bankConfirmLabel}>{CHIME_CONFIRMATION_LABEL}</Text>
                </TouchableOpacity>
            )}
        </View>
    );
}

// ---------------------------------------------------------------------------
// Main Renderer
// ---------------------------------------------------------------------------

export function OnboardingItemRenderer({
    item,
    value,
    onChange,
    readOnly,
    hideStatus,
    rnId,
    onUploadComplete,
    blockedReason,
    bankConfirmed,
    onBankConfirm
}: OnboardingItemProps) {
    const submissionStatus = item.submission?.status;

    //  Computed for every item and answered by the shared rule, which returns
    //  null for anything that is not a bank name or routing number. Doing it
    //  here rather than inside one input covers all three shapes the Chime
    //  answer can take — the bank dropdown, the "Other" free-text box and the
    //  routing number — without touching any of them.
    const bankCheck = checkBankAnswer(item.name, value);

    // Determine component based on item type
    let content: React.ReactNode;

    switch (item.itemType) {
        case "radio":
            content = (
                <RadioInput
                    options={item.options}
                    value={value}
                    onChange={onChange}
                    readOnly={readOnly}
                />
            );
            break;
        case "checkbox":
            content = (
                <CheckboxInput
                    options={item.options}
                    value={value}
                    onChange={onChange}
                    readOnly={readOnly}
                />
            );
            break;
        case "dropdown":
            content = (
                <DropdownInput
                    options={item.options}
                    value={value}
                    onChange={onChange}
                    readOnly={readOnly}
                />
            );
            break;
        case "yes_no":
            content = (
                <RadioInput
                    options={YES_NO_OPTIONS}
                    value={value}
                    onChange={onChange}
                    readOnly={readOnly}
                />
            );
            break;
        case "text":
            if (item.aiConfig?.verificationMethod === "crawler") {
                content = (
                    <LicenseInputWithValidation
                        item={item}
                        value={value}
                        onChange={onChange}
                        readOnly={readOnly}
                        rnId={rnId}
                    />
                );
            } else {
                content = (
                    <BasicTextInput
                        value={value}
                        onChange={onChange}
                        readOnly={readOnly}
                        autoCapitalize="words"
                    />
                );
            }
            break;
        case "email":
            if (item.aiConfig?.verificationMethod === "email_verification") {
                content = (
                    <EmailInputWithValidation
                        item={item}
                        value={value}
                        onChange={onChange}
                        readOnly={readOnly}
                        rnId={rnId}
                    />
                );
            } else {
                content = (
                    <BasicTextInput
                        value={value}
                        onChange={onChange}
                        placeholder="email@example.com"
                        readOnly={readOnly}
                        keyboardType="email-address"
                        autoCapitalize="none"
                    />
                );
            }
            break;
        case "ssn":
            content = <SSNInput value={value} onChange={onChange} readOnly={readOnly} />;
            break;
        case "phone":
            content = (
                <BasicTextInput
                    value={value}
                    onChange={onChange}
                    placeholder="(555) 555-1234"
                    readOnly={readOnly}
                    keyboardType="phone-pad"
                />
            );
            break;
        case "numeric":
            content = (
                <BasicTextInput
                    value={value}
                    onChange={onChange}
                    readOnly={readOnly}
                    keyboardType="numeric"
                />
            );
            break;
        case "date":
            content = <DateInput value={value} onChange={onChange} readOnly={readOnly} />;
            break;
        // A compliance_form declaration (flu / Hep B / TB) is "read the statement,
        // then sign it" — its declaration text renders above as the item
        // description; capture the acknowledgment with the same signature pad
        // instead of falling through to a plain text input.
        case "signature":
        case "compliance_form":
            content = (
                <SignatureInput
                    value={value}
                    onChange={onChange}
                    readOnly={readOnly}
                    signed={submissionStatus === "approved"}
                />
            );
            break;
        // A course_bundle item is satisfied by FINISHING the bundle, never by an
        // answer she supplies — so there is no input here, just her real
        // progress and a way in. Completion is computed server-side from
        // rn_course_completions.
        case "course_bundle":
            content = <CourseBundleLauncher rnId={rnId} />;
            break;
        case "plain_text":
            content = <PlainTextDisplay item={item} />;
            break;
        case "camera": {
            const cameraUpload = item.submission?.uploads?.find((u) => u.isCurrent);
            content = rnId ? (
                <CameraCapture
                    rnId={rnId}
                    submissionId={item.submission?.id}
                    status={submissionStatus}
                    submittedAt={item.submission?.submittedAt}
                    existingUploadUrl={cameraUpload?.viewUrl ?? cameraUpload?.s3Url}
                    onCaptured={() => {
                        /* upload flow is self-contained — don't send to /answers */
                    }}
                />
            ) : null;
            break;
        }
        case "document_upload": {
            const docUpload = item.submission?.uploads?.find((u) => u.isCurrent);
            content = rnId ? (
                <DocumentUpload
                    rnId={rnId}
                    submissionId={item.submission?.id}
                    itemName={item.name}
                    status={submissionStatus}
                    rejectionReason={item.submission?.rejectionReason}
                    existingUploadUrl={docUpload?.viewUrl ?? docUpload?.s3Url}
                    onUploaded={() => onUploadComplete?.()}
                    disabledReason={blockedReason}
                />
            ) : null;
            break;
        }
        default:
            content = <BasicTextInput value={value} onChange={onChange} readOnly={readOnly} />;
    }

    return (
        <View style={styles.container}>
            <Text style={styles.itemName}>
                {item.name}
                {item.mandatory && <Text style={styles.required}> *</Text>}
            </Text>
            {item.description &&
                item.itemType !== "plain_text" &&
                // Defense-in-depth: never render a description that is actually a
                // raw DB timestamp (corrupt seed data leaked created/updated_at
                // into ob_items.description). The migration nulls these; this
                // guards any that slip through.
                !/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(item.description) && (
                    <LinkifiedText text={item.description} style={styles.itemDescription} />
                )}
            {content}
            {bankCheck && (
                <BankAnswerNotice
                    check={bankCheck}
                    confirmed={!!bankConfirmed}
                    onConfirm={(v) => onBankConfirm?.(v)}
                    readOnly={readOnly}
                />
            )}
            {!hideStatus &&
                submissionStatus &&
                item.aiConfig?.verificationMethod &&
                item.aiConfig.verificationMethod !== "none" &&
                item.itemType !== "document_upload" &&
                item.itemType !== "camera" &&
                item.aiConfig.verificationMethod !== "crawler" &&
                item.aiConfig.verificationMethod !== "email_verification" && (
                    <VerificationIndicator status={submissionStatus} />
                )}
            {!hideStatus &&
                item.submission?.rejectionReason &&
                item.aiConfig?.verificationMethod &&
                item.aiConfig.verificationMethod !== "none" &&
                item.itemType !== "document_upload" &&
                item.itemType !== "camera" &&
                item.aiConfig.verificationMethod !== "crawler" &&
                item.aiConfig.verificationMethod !== "email_verification" && (
                    <View style={styles.rejectionBox}>
                        <Text style={styles.rejectionText}>{item.submission.rejectionReason}</Text>
                    </View>
                )}
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        paddingVertical: 14,
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderBottomColor: colors.surfaceBorder
    },
    itemName: {
        fontSize: 15,
        fontWeight: "600",
        color: colors.textPrimary,
        marginBottom: 4
    },
    itemDescription: {
        fontSize: 12,
        color: colors.textSecondary,
        marginBottom: 8
    },
    link: {
        color: colors.teal600,
        textDecorationLine: "underline"
    },
    required: {
        color: colors.error
    },
    optionsContainer: {
        gap: 6,
        marginTop: 4
    },
    radioRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: colors.surfaceBorder
    },
    radioRowSelected: {
        borderColor: colors.primary,
        backgroundColor: colors.primaryLight
    },
    radioCircle: {
        width: 20,
        height: 20,
        borderRadius: 10,
        borderWidth: 2,
        borderColor: colors.surfaceBorder,
        alignItems: "center",
        justifyContent: "center"
    },
    radioCircleSelected: {
        borderColor: colors.primary
    },
    radioDot: {
        width: 10,
        height: 10,
        borderRadius: 5,
        backgroundColor: colors.primary
    },
    radioLabel: {
        fontSize: 14,
        color: colors.textPrimary
    },
    checkbox: {
        width: 20,
        height: 20,
        borderRadius: 4,
        borderWidth: 2,
        borderColor: colors.surfaceBorder,
        alignItems: "center",
        justifyContent: "center"
    },
    checkboxSelected: {
        backgroundColor: colors.primary,
        borderColor: colors.primary
    },
    viewMoreButton: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 4,
        paddingVertical: 10
    },
    viewMoreText: {
        fontSize: 14,
        fontWeight: "600",
        color: colors.primary
    },
    textInput: {
        borderWidth: 1,
        borderColor: colors.surfaceBorder,
        borderRadius: 10,
        paddingHorizontal: 14,
        paddingVertical: 12,
        fontSize: 15,
        color: colors.textPrimary,
        backgroundColor: colors.background
    },
    textInputReadOnly: {
        backgroundColor: colors.backgroundSecondary,
        color: colors.textSecondary
    },
    plainTextContainer: {
        backgroundColor: colors.backgroundSecondary,
        borderRadius: 10,
        padding: 14
    },
    plainText: {
        fontSize: 13,
        color: colors.textSecondary,
        lineHeight: 20
    },
    signButton: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        backgroundColor: colors.primary,
        borderRadius: 10,
        paddingVertical: 12
    },
    signButtonText: {
        fontSize: 14,
        fontWeight: "600",
        color: "#FFFFFF"
    },
    signedContainer: {
        alignItems: "center",
        gap: 8,
        paddingVertical: 8
    },
    signedBadge: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        backgroundColor: "#E8F5E9",
        paddingHorizontal: 16,
        paddingVertical: 10,
        borderRadius: 10
    },
    signedText: {
        fontSize: 15,
        fontWeight: "600",
        color: colors.success
    },
    signaturePreview: {
        width: "100%",
        height: 100,
        borderRadius: 8,
        backgroundColor: colors.backgroundSecondary
    },
    resignButton: {
        paddingVertical: 6,
        paddingHorizontal: 16,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: colors.primary
    },
    resignText: {
        fontSize: 13,
        fontWeight: "600",
        color: colors.primary
    },
    uploadPlaceholder: {
        alignItems: "center",
        gap: 8,
        paddingVertical: 24,
        borderWidth: 1,
        borderStyle: "dashed",
        borderColor: colors.surfaceBorder,
        borderRadius: 10
    },
    uploadPlaceholderText: {
        fontSize: 13,
        color: colors.textTertiary
    },
    emailSentBanner: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 8,
        backgroundColor: colors.successLight,
        borderRadius: 10,
        padding: 12,
        marginTop: 8
    },
    emailSentBannerText: {
        flex: 1,
        fontSize: 13,
        lineHeight: 18,
        color: colors.success600
    },
    verificationRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        marginTop: 6
    },
    verificationText: {
        fontSize: 12,
        fontWeight: "500"
    },
    rejectionBox: {
        backgroundColor: colors.errorLight,
        borderRadius: 8,
        padding: 10,
        marginTop: 6
    },
    bankNoticeBox: {
        borderLeftWidth: 3,
        borderRadius: 8,
        padding: 12,
        marginTop: 8
    },
    bankNoticeBlock: {
        backgroundColor: colors.errorLight,
        borderLeftColor: colors.error600
    },
    bankNoticeWarn: {
        backgroundColor: colors.warningLight,
        borderLeftColor: colors.warning600
    },
    bankNoticeRow: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 8
    },
    bankNoticeText: {
        flex: 1,
        fontSize: 13,
        lineHeight: 18,
        fontWeight: "600"
    },
    bankConfirmRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        marginTop: 12,
        paddingTop: 10,
        borderTopWidth: 1,
        borderTopColor: colors.warning400
    },
    bankConfirmLabel: {
        flex: 1,
        fontSize: 13,
        fontWeight: "600",
        color: colors.warning600
    },
    rejectionText: {
        fontSize: 12,
        color: colors.error
    },
    validateButton: {
        backgroundColor: colors.primary,
        borderRadius: 10,
        paddingVertical: 12,
        alignItems: "center",
        justifyContent: "center",
        marginTop: 10
    },
    validateButtonDisabled: {
        backgroundColor: colors.textTertiary
    },
    validateButtonText: {
        fontSize: 14,
        fontWeight: "600",
        color: "#FFFFFF"
    },
    emailError: {
        fontSize: 12,
        color: colors.error,
        marginTop: 6
    },
    emailSuggestion: {
        fontSize: 12,
        color: colors.textSecondary,
        marginTop: 6
    },
    emailSuggestionStrong: {
        fontWeight: "700",
        color: colors.teal600
    },
    emailVerifiedRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        marginTop: 8
    },
    emailStatusText: {
        fontSize: 13,
        fontWeight: "500"
    },
    resendLink: {
        marginTop: 8,
        paddingVertical: 4
    },
    resendText: {
        fontSize: 13,
        fontWeight: "600",
        color: colors.primary
    }
});

const modalStyles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: "rgba(0,0,0,0.5)",
        justifyContent: "center",
        alignItems: "center",
        padding: 24
    },
    card: {
        backgroundColor: "#FFFFFF",
        borderRadius: 20,
        padding: 28,
        width: "100%",
        maxWidth: 340,
        alignItems: "center",
        position: "relative"
    },
    closeButton: {
        position: "absolute",
        top: 12,
        right: 12,
        width: 32,
        height: 32,
        borderRadius: 16,
        backgroundColor: colors.backgroundSecondary,
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1
    },
    iconCircle: {
        width: 72,
        height: 72,
        borderRadius: 36,
        backgroundColor: colors.primaryLight,
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 16
    },
    title: {
        fontSize: 20,
        fontWeight: "700",
        color: colors.textPrimary,
        marginBottom: 10,
        textAlign: "center"
    },
    body: {
        fontSize: 14,
        color: colors.textSecondary,
        textAlign: "center",
        lineHeight: 20,
        marginBottom: 6
    },
    highlight: {
        fontWeight: "700",
        color: colors.textPrimary
    },
    hint: {
        fontSize: 13,
        color: colors.textTertiary,
        textAlign: "center",
        lineHeight: 19,
        marginBottom: 20
    },
    button: {
        backgroundColor: colors.primary,
        borderRadius: 12,
        paddingVertical: 12,
        paddingHorizontal: 40,
        alignItems: "center"
    },
    buttonText: {
        fontSize: 15,
        fontWeight: "600",
        color: "#FFFFFF"
    }
});
